import React, { useState } from "react";
import { Pencil, X, Check } from "lucide-react";
import { Tile } from "../../Primitives";
import ReasonModal from "./ReasonModal";
import WhatIfPreview from "./WhatIfPreview";
import { relTime } from "../../apikeys/shared";
import { actorName } from "../../modules/util";
import { putSetting } from "../../../lib/classificationApi";
import { cn } from "../../../lib/utils";

const GROUPS = [
  ["Pipeline thresholds", s => !s.key.startsWith("service_level") && !s.key.startsWith("replacement_rate.")],
  ["Service levels by ABC class", s => s.key.startsWith("service_level")],
  ["Install base · annual replacement rate by part family", s => s.key.startsWith("replacement_rate.")],
];
const pctUnit = (u) => u === "probability" || u === "share of units / year";
export const showValue = (s, v = s.value) => (pctUnit(s.unit) ? `${+(v * 100).toFixed(2)}%` : s.unit === "°C" ? `${v}°C` : `${v}`);
const slug = (k) => k.replace(/[._]/g, "-");

function SettingRow({ s, canEdit, onSave }) {
  const [draft, setDraft] = useState(null);
  const editing = draft !== null;
  const tid = `setting-${slug(s.key)}`;
  const lc = s.last_change;
  return (
    <div data-testid={tid} className="px-4 py-3 border-b border-border/70 last:border-b-0 flex flex-col md:flex-row md:items-center gap-2 md:gap-4 hover:bg-surface-2/30 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] font-medium">{s.label}</div>
        <div className="text-[10.5px] text-subtle">
          {s.unit} · range {s.min}–{s.max} · default {showValue(s, s.default)}
          {lc ? <> · last changed by {actorName(lc.actor)} {relTime(lc.ts)} — <span className="italic">“{lc.reason}”</span></> : " · never changed"}
        </div>
      </div>
      {!editing ? (
        <div className="flex items-center gap-3 shrink-0">
          <span data-testid={`${tid}-value`} className={cn("font-mono-tabular text-[14px] font-semibold", s.value !== s.default && "text-primary")}>{showValue(s)}</span>
          {canEdit && (
            <button data-testid={`${tid}-edit`} onClick={() => setDraft(String(s.value))}
                    className="w-7 h-7 inline-flex items-center justify-center border border-border rounded-sm hover:border-primary hover:text-primary transition-colors"><Pencil size={11} /></button>
          )}
        </div>
      ) : (
        <form className="flex items-center gap-1.5 shrink-0" onSubmit={e => { e.preventDefault(); onSave(s, draft, () => setDraft(null)); }}>
          <input data-testid={`${tid}-input`} type="number" step="any" min={s.min} max={s.max} value={draft} autoFocus
                 onChange={e => setDraft(e.target.value)}
                 className="w-28 bg-bg border border-primary px-2 py-1 rounded-sm text-[12px] font-mono-tabular focus:outline-none" />
          <span className="text-[10px] text-subtle w-14">{pctUnit(s.unit) ? "0–1" : s.unit}</span>
          <button type="submit" data-testid={`${tid}-save`} disabled={draft === "" || Number(draft) === s.value}
                  className="w-7 h-7 inline-flex items-center justify-center bg-primary text-primary-fg rounded-sm disabled:opacity-40"><Check size={12} /></button>
          <button type="button" data-testid={`${tid}-cancel`} onClick={() => setDraft(null)}
                  className="w-7 h-7 inline-flex items-center justify-center border border-border rounded-sm hover:bg-surface-2"><X size={12} /></button>
        </form>
      )}
    </div>
  );
}

// Editable pipeline settings (IT Head / COO); read-only for everyone else.
export default function SettingsEditor({ settings, canEdit, actor, onChanged }) {
  const [pending, setPending] = useState(null);
  const [previewed, setPreviewed] = useState(false);
  const close = () => { setPending(null); setPreviewed(false); };
  return (
    <>
      {GROUPS.map(([title, pick]) => {
        const rows = settings.filter(pick);
        return (
          <Tile key={title} dense testid={`settings-group-${slug(title.split(" ")[0].toLowerCase())}`} title={title}
                action={<span className="text-[10px] text-subtle font-mono-tabular">{rows.length} settings</span>}>
            {rows.map(s => <SettingRow key={s.key} s={s} canEdit={canEdit} onSave={(st, v, done) => setPending({ s: st, value: Number(v), done })} />)}
          </Tile>
        );
      })}
      {pending && (
        <ReasonModal title={`Change ${pending.s.label}`} testid="setting-confirm" onClose={close} wide
                     blocked={previewed ? null : "Review the what-if preview before confirming."}
                     impact={<>
                       <div className="font-mono-tabular"><span className="text-muted">{pending.s.label}:</span> {showValue(pending.s)} → <strong className="text-primary">{showValue(pending.s, pending.value)}</strong></div>
                       <div className="text-muted">Applies network-wide and to every branch view. The pipeline re-runs immediately; the matrix, the drill-down and the engines' features update.</div>
                     </>}
                     onConfirm={async (reason) => {
                       const res = await putSetting(pending.s.key, pending.value, reason, actor);
                       pending.done();
                       onChanged(res, `${pending.s.label} set to ${showValue(pending.s, pending.value)}`);
                     }}>
          <WhatIfPreview setting={pending.s} value={pending.value} actor={actor} onReady={setPreviewed} />
        </ReasonModal>
      )}
    </>
  );
}
